import { useCallback, useState } from "react";

import { NewProperty, SelectedLandlord } from "../types";
import {
  PROPERTY_FIELD_KEYS,
  PropertyFieldKey,
  arePropertyFieldsValid,
  getPropertyFieldError,
} from "../../../utils/propertyValidation";

const INITIAL_PROPERTY_STATE: NewProperty = {
  uso: "",
  alias: "",
  label: "",
  direccion: "",
  colonia: "",
  rental_price: "",
  maintenance_price: "",
  street: "",
  exterior_number: "",
  interior_number: "",
  zip_code: "",
  municipality: "",
  city: "",
  state: "",
};

const INITIAL_LANDLORD_STATE: SelectedLandlord = {};

interface UsePropertyRegistrationFormReturn {
  // Form state
  newProperty: NewProperty;
  setNewProperty: (
    property: NewProperty | ((prev: NewProperty) => NewProperty),
  ) => void;
  selectedLandlord: SelectedLandlord;
  setSelectedLandlord: (landlord: SelectedLandlord) => void;

  // Form actions
  resetForm: () => void;
  updatePropertyField: (field: keyof NewProperty, value: string) => void;

  // Validation
  validateStep1: () => boolean;
  hasFormBeenModified: () => boolean;
  getFieldError: (field: PropertyFieldKey) => string | null;
  setFieldTouched: (field: PropertyFieldKey) => void;
  validateAllFields: () => void;
  touchedFields: Set<PropertyFieldKey>;
}

export const usePropertyRegistrationForm =
  (): UsePropertyRegistrationFormReturn => {
    const [newProperty, setNewProperty] = useState<NewProperty>(
      INITIAL_PROPERTY_STATE,
    );
    const [selectedLandlord, setSelectedLandlord] = useState<SelectedLandlord>(
      INITIAL_LANDLORD_STATE,
    );
    const [touchedFields, setTouchedFields] =
      useState<Set<PropertyFieldKey>>(new Set());

    // Reset form to initial state
    const resetForm = useCallback(() => {
      setNewProperty(INITIAL_PROPERTY_STATE);
      setSelectedLandlord(INITIAL_LANDLORD_STATE);
      setTouchedFields(new Set());
    }, []);

    // Update a specific property field and handle derived fields
    const updatePropertyField = useCallback(
      (field: keyof NewProperty, value: string) => {
        setNewProperty((prev) => {
          const updated = { ...prev, [field]: value };

          // Update derived fields
          if (field === "alias") {
            updated.label = value;
          } else if (field === "street" || field === "exterior_number") {
            updated.direccion =
              `${updated.street || ""} ${updated.exterior_number || ""}`.trim();
          }

          return updated;
        });
      },
      [],
    );

    // Validate step 1 of property registration
    const validateStep1 = useCallback(() => {
      return arePropertyFieldsValid(newProperty, { requireUso: true });
    }, [newProperty]);

    // Check if form has been modified from initial state
    const hasFormBeenModified = useCallback(() => {
      const hasPropertyData = Object.keys(newProperty).some((key) => {
        const propertyKey = key as keyof NewProperty;
        const value = newProperty[propertyKey];
        const initialValue = INITIAL_PROPERTY_STATE[propertyKey];
        return value !== initialValue && value?.trim() !== "";
      });

      const hasLandlordData = Object.keys(selectedLandlord).length > 0;

      return hasPropertyData || hasLandlordData;
    }, [newProperty, selectedLandlord]);

    // Set a field as touched
    const setFieldTouched = useCallback((field: PropertyFieldKey) => {
      setTouchedFields((prev) => {
        const next = new Set(prev);
        next.add(field);
        return next;
      });
    }, []);

    // Validate all fields (mark all as touched)
    const validateAllFields = useCallback(() => {
      setTouchedFields(new Set(PROPERTY_FIELD_KEYS));
    }, []);

    // Get validation error for a specific field
    const getFieldError = useCallback(
      (field: PropertyFieldKey): string | null => {
        if (!touchedFields.has(field)) {
          return null;
        }

        return (
          getPropertyFieldError(field, newProperty, { requireUso: true }) ?? null
        );
      },
      [newProperty, touchedFields],
    );

    return {
      newProperty,
      setNewProperty,
      selectedLandlord,
      setSelectedLandlord,
      resetForm,
      updatePropertyField,
      validateStep1,
      hasFormBeenModified,
      getFieldError,
      setFieldTouched,
      validateAllFields,
      touchedFields,
    };
  };
